// =============================================================
// קובץ: C:\gocode\projects\cartax\git\cartax_fe\src\components\backoffice\epa-data\EPADataPage.tsx
// =============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  epaDataService, 
  EpaYearInfo, 
  EpaStatsData 
} from '../../../services/backoffice/epaDataService';

const EPADataPage: React.FC = () => {
  // State
  const [stats, setStats] = useState<EpaStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [targetYear, setTargetYear] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [yearToDelete, setYearToDelete] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await epaDataService.getEpaStats();
      setStats(data);
      
    } catch (error: any) {
      if (error.message === 'Session expired - redirecting to login') {
        return;
      }
      setError(`שגיאה בטעינת הנתונים: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
        setError('יש לבחור קובץ Excel (.xlsx, .xls) או CSV');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('יש לבחור קובץ להעלאה');
      return;
    }

    // Check if we'll exceed 2 years
    const currentYears = stats?.years.length || 0;
    const yearNum = targetYear ? parseInt(targetYear) : null;
    const isNewYear = yearNum && !stats?.years.find(y => y.model_year === yearNum);
    
    if (currentYears >= 2 && isNewYear) {
      const oldestYear = stats?.years[stats.years.length - 1]?.model_year;
      if (!window.confirm(
        `⚠️ שים לב: המערכת מוגבלת ל-2 שנים.\n\n` +
        `העלאת שנת ${yearNum} תגרום למחיקת שנת ${oldestYear}.\n\n` +
        `האם להמשיך?`
      )) {
        return;
      }
    }

    try {
      setUploading(true);
      setError('');
      
      const result = await epaDataService.uploadEpaFile(
        selectedFile, 
        yearNum || undefined
      );
      
      setSuccessMessage(
        `✅ הקובץ הועלה בהצלחה!\n` +
        `נוספו: ${result.inserted_count} | עודכנו: ${result.updated_count} | דולגו: ${result.skipped_count}` +
        (result.deleted_year ? ` | שנת ${result.deleted_year} נמחקה` : '')
      );
      
      // Reset form
      setSelectedFile(null);
      setTargetYear('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Reload stats
      await loadStats();
      
    } catch (error: any) {
      setError(`שגיאה בהעלאת הקובץ: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Handle delete year
  const handleDeleteYear = (year: number) => {
    setYearToDelete(year);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!yearToDelete) return;
    
    try {
      setDeleting(true);
      const result = await epaDataService.deleteEpaYear(yearToDelete);
      setSuccessMessage(`✅ שנת ${yearToDelete} נמחקה בהצלחה (${result.deleted_count} רשומות)`);
      setShowDeleteConfirm(false);
      setYearToDelete(null);
      await loadStats();
    } catch (error: any) {
      setError(`שגיאה במחיקת השנה: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Generate year options for dropdown (current year + 1 down to 2020)
  const yearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear + 3; y >= currentYear - 2; y--) {
      years.push(y);
    }
    return years;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען נתוני EPA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">נתוני EPA</h1>
          <p className="text-gray-600 mt-1">ניהול נתוני זיהום רכבים לפי תקן אמריקאי</p>
        </div>
        <div className="text-sm text-gray-500">
          סה"כ: {stats?.total_records?.toLocaleString() || 0} רשומות | {stats?.total_makes || 0} יצרנים
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg whitespace-pre-line">
          {successMessage}
        </div>
      )}

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <span className="ml-2">📤</span>
          העלאת קובץ EPA
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              קובץ Excel / CSV
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
              disabled={uploading}
            />
          </div>
          
          {/* Year Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              שנת יעד (אופציונלי)
            </label>
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
              disabled={uploading}
            >
              <option value="">אוטומטי מהקובץ</option>
              {yearOptions().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          {/* Upload Button */}
          <div>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                !selectedFile || uploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {uploading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 ml-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  מעלה...
                </span>
              ) : (
                'העלה קובץ'
              )}
            </button>
          </div>
        </div>
        
        {selectedFile && (
          <div className="mt-3 text-sm text-gray-600">
            📎 קובץ נבחר: <span className="font-medium">{selectedFile.name}</span>
            <span className="text-gray-400 mr-2">
              ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
        )}
        
        {/* Warning about 2-year limit */}
        {stats && stats.years.length >= 2 && (
          <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg text-sm">
            ⚠️ <strong>שים לב:</strong> המערכת מוגבלת ל-2 שנים. העלאת שנה חדשה תגרום למחיקה אוטומטית של השנה הישנה ביותר.
          </div>
        )}
      </div>

      {/* Years Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-4">שנים במערכת ({stats?.years.length || 0}/2)</h2>
        
        {stats?.years.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            <span className="text-4xl block mb-4">📂</span>
            <p>אין נתונים במערכת</p>
            <p className="text-sm mt-2">העלה קובץ EPA כדי להתחיל</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats?.years.map((yearInfo: EpaYearInfo) => (
              <div 
                key={yearInfo.model_year}
                className="bg-white rounded-lg shadow p-6 border-r-4 border-blue-500"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-600">
                      {yearInfo.model_year}
                    </h3>
                    <div className="mt-3 space-y-1 text-sm text-gray-600">
                      <p>
                        <span className="font-medium">רשומות:</span>{' '}
                        {yearInfo.record_count?.toLocaleString()}
                      </p>
                      <p>
                        <span className="font-medium">יצרנים:</span>{' '}
                        {yearInfo.makes_count}
                      </p>
                      {yearInfo.upload_date && (
                        <p>
                          <span className="font-medium">הועלה:</span>{' '}
                          {new Date(yearInfo.upload_date).toLocaleDateString('he-IL')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleDeleteYear(yearInfo.model_year)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
                    title="מחק שנה"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              ⚠️ אישור מחיקה
            </h3>
            <p className="text-gray-600 mb-6">
              האם אתה בטוח שברצונך למחוק את כל נתוני שנת <strong>{yearToDelete}</strong>?
              <br />
              <span className="text-red-600 text-sm">פעולה זו אינה ניתנת לביטול.</span>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setYearToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={deleting}
              >
                ביטול
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-300"
              >
                {deleting ? 'מוחק...' : 'מחק'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EPADataPage;